import { useState } from "react";
import { MessageCircle, X, Volume2 } from "lucide-react";

const MASCOT = "https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/r4vq4787_HeylpMate_Sticker_09.png";
const VOLUNTEER_WA = "919004398004"; // admin-editable later via platform settings

/**
 * FreeHelpMascot — floating bottom-right mascot CTA.
 * Opens WhatsApp to a REAL HUMAN volunteer (not AI) with a prefilled friendly message.
 */
export default function FreeHelpMascot() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);

  const openWa = (intent = "free_help") => {
    const messages = {
      free_help: "Namaste! I need some *free help* from a real HeylpMate volunteer. Could you please guide me?",
      celebrity: "Hi HeylpMate! I'm trying to reach a social human / celebrity / entrepreneur who can help with a specific question — can you connect me?",
      humor: "Hi! Life's a bit heavy today — can a HeylpMate volunteer send me a smile or just chat kindly for 2 minutes? 🙏",
    };
    const url = `https://wa.me/${VOLUNTEER_WA}?text=${encodeURIComponent(messages[intent])}`;
    window.open(url, "_blank");
    setSent(true);
    setTimeout(() => setOpen(false), 400);
  };

  return (
    <>
      {/* Floating button */}
      <button data-testid="free-help-mascot-btn"
              onClick={() => setOpen(true)}
              className="fixed bottom-5 left-5 z-40 group"
              aria-label="Free Help from a real HeylpMate volunteer">
        <div className="relative">
          <div className="absolute inset-0 rounded-full animate-ping"
               style={{ background: "#25D36644" }} />
          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shadow-2xl border-2"
               style={{ borderColor: "#25D366", background: "#fff" }}>
            <img src={MASCOT} alt="HeylpMate Free Help" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -top-1 -right-10 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-bold text-white shadow-lg"
               style={{ background: "#25D366" }}>
            Free Help 💚
          </div>
        </div>
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-end md:place-items-center p-4"
             onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl max-w-sm w-full p-5 shadow-2xl relative"
               onClick={(e) => e.stopPropagation()}
               data-testid="free-help-panel">
            <button onClick={() => setOpen(false)}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center bg-neutral-100">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <img src={MASCOT} className="w-14 h-14 rounded-full" alt="mascot" />
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#F45118" }}>Free Help · No AI</div>
                <div className="font-display text-lg font-bold" style={{ color: "#00604A" }}>
                  Talk to a real HeylpMate human.
                </div>
                <div className="text-[11px]" style={{ color: "#667085" }}>
                  Volunteers, celebrities, politicians, entrepreneurs — anyone with a good heart & good humour.
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <button data-testid="free-help-general" onClick={() => openWa("free_help")}
                      className="w-full rounded-2xl p-3 text-left border-2 flex items-center gap-3 hover:bg-emerald-50 transition"
                      style={{ borderColor: "#25D36644" }}>
                <MessageCircle className="w-5 h-5" style={{ color: "#25D366" }} />
                <div>
                  <div className="text-sm font-bold" style={{ color: "#00604A" }}>I just need friendly help</div>
                  <div className="text-[10px]" style={{ color: "#667085" }}>A real human replies within minutes</div>
                </div>
              </button>
              <button data-testid="free-help-celeb" onClick={() => openWa("celebrity")}
                      className="w-full rounded-2xl p-3 text-left border-2 flex items-center gap-3 hover:bg-orange-50 transition"
                      style={{ borderColor: "#F4511844" }}>
                <Volume2 className="w-5 h-5" style={{ color: "#F45118" }} />
                <div>
                  <div className="text-sm font-bold" style={{ color: "#00604A" }}>Reach a social human / celebrity</div>
                  <div className="text-[10px]" style={{ color: "#667085" }}>We try — no guarantees, no bots</div>
                </div>
              </button>
              <button data-testid="free-help-humor" onClick={() => openWa("humor")}
                      className="w-full rounded-2xl p-3 text-left border-2 flex items-center gap-3 hover:bg-yellow-50 transition"
                      style={{ borderColor: "#F59E0B44" }}>
                <span className="text-xl">😄</span>
                <div>
                  <div className="text-sm font-bold" style={{ color: "#00604A" }}>Just need a smile today</div>
                  <div className="text-[10px]" style={{ color: "#667085" }}>Someone with great humour replies</div>
                </div>
              </button>
            </div>
            {sent && <div className="mt-3 text-center text-xs font-bold" style={{ color: "#25D366" }}>Opened WhatsApp 💚</div>}
          </div>
        </div>
      )}
    </>
  );
}
